import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Loader2, UploadCloud, X } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { FileTypeIcon } from "@/shared/components/files/FileTypeIcon";
import {
  DROPZONE_ACCEPT,
  MAX_DOCUMENT_SIZE_BYTES,
  inspectDocumentFile,
  type DocumentKind,
  type DocumentRejection,
} from "@/shared/utils/documentFileTypes";

interface FileUploadProps {
  value: File | null;
  kind: DocumentKind | null;
  /** Mensagem de recusa já traduzida (formato, tamanho, arquivo corrompido). */
  error?: string | null;
  onFileAccepted: (file: File, kind: DocumentKind) => void;
  onFileRejected: (reason: DocumentRejection) => void;
  onRemove: () => void;
  className?: string;
}

function formatBytes(bytes: number, decimals = 1) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * PBI 87080 — área de envio do formulário único da Storage: arrastar e soltar ou
 * selecionar. O tipo é reconhecido aqui (extensão + assinatura) e o arquivo só
 * chega ao formulário quando é aceito; a recusa sobe como motivo.
 */
export function FileUpload({
  value,
  kind,
  error,
  onFileAccepted,
  onFileRejected,
  onRemove,
  className,
}: FileUploadProps) {
  const { t } = useTranslation();
  const [isInspecting, setIsInspecting] = useState(false);

  // O `accept` do dropzone só filtra o seletor nativo; a validação é sempre a
  // nossa, para a recusa vir com o motivo certo também no arrastar e soltar.
  const onDrop = useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      const file = accepted[0] ?? rejected[0]?.file;
      if (!file) return;
      setIsInspecting(true);
      try {
        const inspection = await inspectDocumentFile(file);
        if (inspection.ok) onFileAccepted(file, inspection.kind);
        else onFileRejected(inspection.reason);
      } finally {
        setIsInspecting(false);
      }
    },
    [onFileAccepted, onFileRejected],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    multiple: false,
    noClick: !!value,
    noKeyboard: !!value,
  });

  return (
    <div className={cn("w-full", className)}>
      <div
        {...getRootProps()}
        data-testid="document-dropzone"
        className={cn(
          "relative border-2 border-dashed rounded-lg p-6 transition-colors",
          isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25",
          error ? "border-destructive/50 bg-destructive/5" : "",
          value ? "bg-primary/5" : "hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
        )}
      >
        <input {...getInputProps()} aria-label={t("knowledge.imports.upload.selectButton")} />

        {value && kind ? (
          <div className="flex items-center gap-3 pr-8">
            <FileTypeIcon kind={kind} tile="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" title={value.name}>
                {value.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(`knowledge.imports.upload.types.${kind}`)} · {formatBytes(value.size)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2"
              aria-label={t("knowledge.imports.upload.removeFile")}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            {isInspecting ? (
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            ) : (
              <UploadCloud className="h-10 w-10 text-muted-foreground" />
            )}
            <p className="text-sm font-medium">{t("knowledge.imports.upload.dragText")}</p>
            <p className="text-xs text-muted-foreground">{t("knowledge.imports.upload.orText")}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              {t("knowledge.imports.upload.selectButton")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t("knowledge.imports.upload.maxSizeText", {
                size: formatBytes(MAX_DOCUMENT_SIZE_BYTES),
              })}
            </p>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
